import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import styles from './ScrollVideoHero.module.css';

export interface HeroChapter {
  step: string;
  title: string;
  text: string;
}

interface ScrollVideoHeroProps {
  /** Video encoded with a keyframe on every frame, so seeking stays smooth. */
  src: string;
  poster: string;
  /** Portrait framing for phones; `src` is cropped instead when absent. */
  portrait?: { src: string; poster: string };
  /**
   * Horizontal position of the subject, in % of the landscape frame, at the
   * first and the last image. Phones crop `src` around it, following the
   * subject as the video plays. Ignored when `portrait` is served.
   */
  portraitFocus?: { from: number; to: number };
  videoLabel: string;
  chapters: readonly HeroChapter[];
  scrollHint: string;
  progressLabel: string;
  /** Actions shown under the last chapter. */
  actions?: ReactNode;
}

/** Share of a chapter spent fading in and fading out. */
const FADE = 0.18;
/** Progress smoothing: 1 follows the finger, 0.1 damps heavily. */
const SMOOTHING = 0.14;

function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Screens where a landscape video would lose most of its picture. */
const PORTRAIT_QUERY = '(orientation: portrait) and (max-width: 899px)';

/**
 * Landing page hero: the video plays at the pace of the scroll.
 *
 * The section is one viewport tall per chapter; the stage sticks to the top
 * while the page scrolls, and the scroll position drives `currentTime`. No
 * library: a requestAnimationFrame loop that only runs while the section is
 * on screen, and styles written straight to the elements so React does not
 * re-render on every frame.
 *
 * Reduced motion: the video is not animated and the chapters stack.
 */
export function ScrollVideoHero({
  src: landscapeSrc,
  poster: landscapePoster,
  portrait,
  portraitFocus,
  videoLabel,
  chapters,
  scrollHint,
  progressLabel,
  actions,
}: ScrollVideoHeroProps) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const portraitScreen = useMediaQuery(PORTRAIT_QUERY);
  const servePortrait = Boolean(portrait && portraitScreen);
  const { src, poster } = servePortrait && portrait ? portrait : { src: landscapeSrc, poster: landscapePoster };
  const focusStyle = (portraitFocus && !servePortrait
    ? { '--hero-focus-from': `${portraitFocus.from}%`, '--hero-focus-to': `${portraitFocus.to}%` }
    : {}) as CSSProperties;
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const chapterRefs = useRef<Array<HTMLDivElement | null>>([]);
  const barRef = useRef<HTMLSpanElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const [activeChapter, setActiveChapter] = useState(0);

  // Safari does not paint the current frame of a video that never played: a
  // play immediately paused unlocks it. The video is muted, so it is allowed.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) return;
    const prime = () => {
      video.play().then(() => video.pause()).catch(() => undefined);
    };
    if (video.readyState >= 1) prime();
    else video.addEventListener('loadedmetadata', prime, { once: true });
    return () => video.removeEventListener('loadedmetadata', prime);
  }, [reducedMotion, src]);

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video || reducedMotion) return;

    const count = chapters.length;
    let raf = 0;
    let running = false;
    let smooth = -1;
    let lastChapter = -1;

    const frame = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const target = travel > 0 ? clamp(-rect.top / travel) : 0;

      smooth = smooth < 0 ? target : smooth + (target - smooth) * SMOOTHING;
      if (Math.abs(target - smooth) < 0.0005) smooth = target;

      // Video: only seek again once the previous seek has finished, otherwise
      // requests pile up and the picture lags behind.
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (duration > 0 && !video.seeking) {
        const time = smooth * (duration - 0.04);
        if (Math.abs(video.currentTime - time) > 1 / 60) video.currentTime = time;
      }
      section.style.setProperty('--hero-progress', smooth.toFixed(4));

      // Chapters: each one takes an equal share of the scroll.
      const position = smooth * count;
      chapterRefs.current.forEach((el, i) => {
        if (!el) return;
        const local = position - i;
        const fadeIn = i === 0 ? 1 : clamp(local / FADE);
        const fadeOut = i === count - 1 ? 1 : clamp((1 - local) / FADE);
        const inside = local >= 0 && (local <= 1 || i === count - 1);
        const opacity = inside ? Math.min(fadeIn, fadeOut) : 0;
        const shift = (1 - opacity) * (local < 0.5 ? 28 : -28);
        el.style.opacity = opacity.toFixed(3);
        el.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0)`;
        el.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
      });

      if (barRef.current) barRef.current.style.transform = `scaleX(${smooth.toFixed(4)})`;
      if (hintRef.current) hintRef.current.style.opacity = clamp(1 - smooth * 12).toFixed(3);

      const chapter = Math.min(count - 1, Math.floor(position));
      if (chapter !== lastChapter) {
        lastChapter = chapter;
        setActiveChapter(chapter);
      }

      if (running) raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (running) return;
      running = true;
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    // The loop only runs while the section is on screen.
    const observer = new IntersectionObserver(
      ([entry]) => (entry?.isIntersecting ? start() : stop()),
      { rootMargin: '10% 0px' },
    );
    observer.observe(section);
    frame();

    return () => {
      observer.disconnect();
      stop();
    };
  }, [chapters.length, reducedMotion]);

  if (reducedMotion) {
    return (
      <section className={styles.static} aria-label={videoLabel} style={focusStyle}>
        <div className={styles.staticMedia}>
          <img src={poster} alt={videoLabel} className={styles.media} />
          <div className={styles.veil} aria-hidden="true" />
        </div>
        <div className={styles.staticChapters}>
          {chapters.map((c, i) => (
            <div key={c.step} className={styles.staticChapter}>
              <p className={styles.step}>{c.step}</p>
              {i === 0 ? <h1 className={styles.title}>{c.title}</h1> : <h2 className={styles.title}>{c.title}</h2>}
              <p className={styles.text}>{c.text}</p>
            </div>
          ))}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={styles.hero}
      style={{ ...focusStyle, '--hero-chapters': chapters.length } as CSSProperties}
    >
      <div className={styles.stage}>
        <video
          ref={videoRef}
          className={styles.media}
          src={src}
          poster={poster}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-label={videoLabel}
        />
        <div className={styles.veil} aria-hidden="true" />

        <div className={styles.chapters}>
          {chapters.map((c, i) => (
            <div
              key={c.step}
              ref={(el) => { chapterRefs.current[i] = el; }}
              className={styles.chapter}
              style={i === 0 ? undefined : { opacity: 0, visibility: 'hidden' }}
            >
              <p className={styles.step}>{c.step}</p>
              {i === 0 ? <h1 className={styles.title}>{c.title}</h1> : <h2 className={styles.title}>{c.title}</h2>}
              <p className={styles.text}>{c.text}</p>
              {i === chapters.length - 1 && actions && <div className={styles.actions}>{actions}</div>}
            </div>
          ))}
        </div>

        <p ref={hintRef} className={styles.hint} aria-hidden="true">
          <span>{scrollHint}</span>
          <span className={styles.hintLine} />
        </p>

        <div className={styles.progress}>
          <ol className={styles.steps} aria-label={progressLabel}>
            {chapters.map((c, i) => (
              <li key={c.step} className={styles.stepDot} aria-current={i === activeChapter ? 'step' : undefined}>
                {c.step}
              </li>
            ))}
          </ol>
          <span className={styles.track} aria-hidden="true">
            <span ref={barRef} className={styles.bar} />
          </span>
        </div>
      </div>
    </section>
  );
}
